import {Vnode} from 'mithril';
import {extend, override} from 'flarum/common/extend';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import ItemList from 'flarum/common/utils/ItemList';
import LogInModal from 'flarum/forum/components/LogInModal';
import SignUpModal from 'flarum/forum/components/SignUpModal';
import ChangeEmailModal from 'flarum/forum/components/ChangeEmailModal';
import NotificationGrid from 'flarum/forum/components/NotificationGrid';
import Discussion from 'flarum/common/models/Discussion';
import User from 'flarum/common/models/User';
import BadgeManager from './BadgeManager';
import TokenRequest from '../common/TokenRequest';
import SensorsHelper from '../common/SensorsHelper';
import OtelHelper from '../common/OtelHelper';
import addFeatureControl from './addFeatureControl';
import addFeatureBadge from './addFeatureBadge';
import addFeaturedFilter from './addFeaturedFilter';
import addNavigationBadges from './addNavigationBadges';
import DiscussionFeaturedNotification from './components/DiscussionFeaturedNotification';
import FeaturedDiscussionAnnouncementNotification from './components/FeaturedDiscussionAnnouncementNotification';
import IndexPage from 'flarum/forum/components/IndexPage';

export {TokenRequest};

const translationPrefix = 'tanka-connector.forum.sign-up.';

app.initializers.add('tanka-connector', () => {

    OtelHelper.init();

    // Add the featured route
    app.routes.featured = { path: '/featured', component: IndexPage };

    // 绑定用户登出事件以跟踪神策数据
    // 使用更安全的方式绑定logout事件
    const bindLogoutEvent = () => {
        if (app.session && typeof app.session.logout === 'function') {
            const originalLogout = app.session.logout.bind(app.session);
            app.session.logout = function(...args) {
                console.log('[Tanka-Connector] 用户正在登出');
                // 调用神策数据的 logout 方法
                SensorsHelper.trackUserLogout();
                // 调用原始的登出方法
                return originalLogout(...args);
            };
            console.log('[Tanka-Connector] 登出事件绑定成功');
            return true;
        }
        return false;
    };

    // 立即尝试绑定
    if (!bindLogoutEvent()) {
        // 如果失败，延迟重试
        setTimeout(() => {
            if (!bindLogoutEvent()) {
                console.warn('[Tanka-Connector] 无法绑定登出事件，app.session.logout不可用');
            }
        }, 1000);
    }

    extend(LogInModal.prototype, 'oninit', function () {
        this.passwordlessCodeSent = false;
        this.passwordlessSkip = !app.forum.attribute('tanka-connector.passwordlessLoginByDefault');
        this.verificationCode = '';
        this.sendCodeCountdown = 0;
        this.countdownTimer = null;
        this.showMainLoginButton = false; // 新增状态：是否显示主登录按钮

        // 设置 Remember Me 默认为勾选状态
        this.remember(true);
    });

    extend(LogInModal.prototype, 'fields', function (items: ItemList<any>) {
        if (this.passwordlessSkip) {
            return;
        }

        if (this.passwordlessCodeSent) {
            const verificationMethod = app.forum.attribute('tanka-connector.verificationMethod');
            const codesentKey = verificationMethod === 'tanka' ? 'code-sent-tanka' : 'code-sent';
            items.add('code-info', m('p.PasswordlessInfo', app.translator.trans(translationPrefix + codesentKey)), 200);

            items.add('verification-code', m('.FormGroup.VerificationCodeGroup', [
                m('input.FormControl', {
                    name: 'verification-code',
                    type: 'text',
                    placeholder: app.translator.trans(translationPrefix + 'verification-code'),
                    value: this.verificationCode,
                    oninput: (e: Event) => {
                        this.verificationCode = (e.target as HTMLInputElement).value;
                    },
                    maxlength: 6,
                }),
                m('.Passwordless-button-container', [
                    m('button.Button--link.Passwordless-send-code-link', {
                        type: 'button',
                        disabled: this.sendCodeCountdown > 0,
                        onclick: this.requestPasswordlessCode.bind(this),
                    }, this.sendCodeCountdown > 0
                        ? app.translator.trans(translationPrefix + 'resend-code', {seconds: this.sendCodeCountdown})
                        : app.translator.trans(translationPrefix + 'send-code')
                    )
                ])
            ]), 150);

            if (items.has('identification')) items.remove('identification');
            if (items.has('remember')) items.remove('remember');
        } else {
            const verificationMethod = app.forum.attribute('tanka-connector.verificationMethod');
            const howItWorksKey = verificationMethod === 'tanka' ? 'how-it-works-tanka' : 'how-it-works';
            items.add('passwordless-info', m('p.PasswordlessInfo', app.translator.trans(translationPrefix + howItWorksKey)), -100);

            if (items.has('identification')) {
                items.remove('identification');
            }

            // Add just the email input field
            items.add('identification', m('.FormGroup', [
                m('label', { for: 'identification' }, '📧 ' + app.translator.trans(translationPrefix + 'email-address')),
                m('input.FormControl#identification', {
                    name: 'identification',
                    type: 'email',
                    placeholder: app.translator.trans(translationPrefix + 'enter-email'),
                    value: this.identification(),
                    oninput: (e: Event) => {
                        this.identification((e.target as HTMLInputElement).value);
                    },
                    required: true,
                })
            ]), 100);

            // Handle the 'Remember Me' separately
            if (items.has('remember')) {
                const rememberVNode = items.get('remember');
                items.remove('remember');

                // Add Remember Me in its own FormGroup
                items.add('passwordless-remember', m('.FormGroup', [
                    rememberVNode
                ]), 20); // Priority 20 is typical for 'remember me'

                // Add Continue/Send Code button in separate FormGroup below Remember Me
                items.add('passwordless-continue', m('.FormGroup', [
                    // 根据状态决定按钮样式和文本
                    this.showMainLoginButton ?
                        m('button.Button--link.Passwordless-send-code-link', {
                            type: 'button',
                            disabled: this.loading || this.sendCodeCountdown > 0,
                            onclick: this.requestPasswordlessCode.bind(this)
                        }, this.sendCodeCountdown > 0
                            ? app.translator.trans(translationPrefix + 'resend-code', {seconds: this.sendCodeCountdown})
                            : app.translator.trans(translationPrefix + 'send-code')
                        ) :
                        m('button.Button.Button--primary.Button--block', {
                            type: 'button',
                            disabled: this.loading || this.sendCodeCountdown > 0,
                            onclick: this.handleContinueClick.bind(this)
                        }, 'Continue')
                ]), 10); // Lower priority so it appears below Remember Me
            }
        }

        if (items.has('password')) items.remove('password');

        // 修改原始的submit按钮显示逻辑
        if (items.has('submit')) {
            if (!this.passwordlessSkip && !this.showMainLoginButton && !this.passwordlessCodeSent) {
                // 初始状态：隐藏Log In按钮
                items.remove('submit');
            }
        }
    });

    extend(LogInModal.prototype, 'footer', function (vdom: Vnode | Vnode[]) {
        if (!vdom) return;

        const children = Array.isArray(vdom) ? vdom : vdom.children;

        if (!Array.isArray(children)) {
            console.warn('[passwordless] skipped LogInModal.footer');
            return;
        }

        if (!this.passwordlessSkip && children.length && children[0]?.attrs?.className === 'LogInModal-forgotPassword') {
            children.splice(0, 1);
        }

        children.push(m('p.LogInModal-footer', app.translator.trans(translationPrefix + (this.passwordlessSkip ? 'login-without-password' : 'login-with-password'), {
            a: m('a', {
                onclick: () => {
                    this.passwordlessSkip = !this.passwordlessSkip;
                    this.passwordlessCodeSent = false;
                    this.verificationCode = '';
                    this.clearCountdown();
                },
            }),
        })));
    });

    // 新增：处理Continue按钮点击
    LogInModal.prototype.handleContinueClick = function (this: LogInModal, event: Event) {
        event.preventDefault();

        if (!this.identification()) {
            this.errors = {identification: [app.translator.trans('validation.required', {attribute: 'identification'})]};
            return;
        }

        // 直接执行发送验证码的逻辑，成功后再显示Send Code按钮
        this.requestPasswordlessCode(event);
    };

    LogInModal.prototype.requestPasswordlessCode = function (this: LogInModal, event: Event) {
        event.preventDefault();

        if (!this.passwordlessCodeSent && !this.identification()) {
            this.errors = {identification: [app.translator.trans('validation.required', {attribute: 'identification'})]};
            return;
        }

        this.loading = true;

        app.request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/tanka-verification-code-request',
            body: {
                identification: this.identification(),
                remember: this.remember(),
            },
            errorHandler: this.onerror.bind(this),
        }).then(() => {
            this.passwordlessCodeSent = true;
            this.showMainLoginButton = true; // 验证码发送成功后才显示Send Code按钮
            this.loading = false;
            this.alertAttrs = null; // 清除错误提示
            this.startCountdown();
            m.redraw();
        }, () => {
            this.loading = false;
            m.redraw();
        });
    };

    LogInModal.prototype.startCountdown = function (this: LogInModal) {
        this.sendCodeCountdown = 30;
        this.countdownTimer = setInterval(() => {
            this.sendCodeCountdown--;
            if (this.sendCodeCountdown <= 0) {
                this.clearCountdown();
            }
            m.redraw();
        }, 1000);
    };

    LogInModal.prototype.clearCountdown = function (this: LogInModal) {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
        }
        this.sendCodeCountdown = 0;
    };

    override(LogInModal.prototype, 'onerror', function (original, error) {
        if (error.status === 401 && error.alert && !this.passwordlessSkip && app.forum.attribute('allowSignUp')) {
            error.alert.controls = error.alert.controls || [];
            error.alert.controls.unshift(app.translator.trans(translationPrefix + 'error-try-signup', {
                a: Button.component({
                    className: 'Button Button--link',
                    onclick: this.signUp.bind(this),
                }),
            }));
        }

        return original(error);
    });

    override(LogInModal.prototype, 'onsubmit', function (original, event) {
        if (this.passwordlessSkip) {
            return original(event);
        }

        event.preventDefault();

        if (!this.passwordlessCodeSent) {
            this.requestPasswordlessCode(event);
            return;
        }

        if (!this.verificationCode) {
            this.errors = {code: [app.translator.trans('validation.required', {attribute: 'verification code'})]};
            return;
        }

        this.loading = true;

        app.request({
            method: 'POST',
            url: app.forum.attribute('apiUrl') + '/tanka-verification-code-login',
            body: {
                email: this.identification(),
                code: this.verificationCode,
            },
            errorHandler: this.onerror.bind(this),
        }).then((response: any) => {
            // 在重新加载页面之前跟踪用户登录
            if (response && response.userId) {
                SensorsHelper.trackUserLogin(response.userId);
            }
            window.location.reload();
        }, () => {
            this.loading = false;
            m.redraw();
        });
    });

    extend(SignUpModal.prototype, 'fields', function (items) {
        if (app.forum.attribute('tanka-connector.hideSignUpPassword') && items.has('password')) {
            items.remove('password');
        }
    });

    extend(ChangeEmailModal.prototype, 'fields', function (items) {
        if (this.success) return;

        if (items.has('email')) items.setPriority('email', 30);
        if (items.has('password')) items.setPriority('password', 20);

        items.add('tanka-verification-code-request', m(TokenRequest, {
            errorHandler: this.onerror.bind(this),
            onErrorClear: () => {
                this.alertAttrs = null;
            },
            additionalText: app.translator.trans('tanka-connector.forum.email-change-note'),
        }), 10);
    });

    // Extend NotificationGrid to add Tanka notification method
    extend(NotificationGrid.prototype, 'notificationMethods', function (items: ItemList<any>) {
        items.add('tanka', {
            name: 'tanka',
            icon: 'icon-tanka-front',
            label: app.translator.trans('tanka-connector.forum.notifications.notify_by_tanka_heading'),
        });
    });

    // Initialize Featured Discussions functionality
    addFeatureControl();
    addFeatureBadge();
    addFeaturedFilter();
    addNavigationBadges();

    // Register Featured notifications
    app.notificationComponents.discussionFeatured = DiscussionFeaturedNotification;
    app.notificationComponents.featuredDiscussionAnnouncement = FeaturedDiscussionAnnouncementNotification;

    // Add Discussion Featured notification types to Notification Grid
    extend(NotificationGrid.prototype, 'notificationTypes', function (items: ItemList<any>) {
        items.add('discussionFeatured', {
            name: 'discussionFeatured',
            icon: app.forum.attribute('tanka-connector.featuredIcon') || 'fas fa-magic',
            label: app.translator.trans('tanka-connector.forum.settings.notify_discussion_featured_label'),
        });

        items.add('featuredDiscussionAnnouncement', {
            name: 'featuredDiscussionAnnouncement',
            icon: app.forum.attribute('tanka-connector.featuredIcon') || 'fas fa-magic',
            label: app.translator.trans('tanka-connector.forum.settings.notify_featured_discussion_announcement_label'),
        });
    });

    // 当讨论被标记为已读（保存 lastReadPostNumber）后，刷新未读角标统计
    override(Discussion.prototype as any, 'save', function (original: any, attributes: any, options: any) {
        const result = original(attributes, options);

        try {
            if (attributes && Object.prototype.hasOwnProperty.call(attributes, 'lastReadPostNumber')) {
                // 在保存成功后刷新角标统计
                Promise.resolve(result).then(() => {
                    if (app.session.user) {
                        BadgeManager.refresh();
                    }
                });
            }
        } catch (e) {
            // 忽略刷新逻辑中的异常，避免影响原有保存流程
        }

        return result;
    });

    // 在工具栏点击刷新按钮后，也刷新未读角标
    extend(IndexPage.prototype, 'actionItems', function (items: ItemList<any>) {
        const refreshItem = items.get('refresh');
        if (refreshItem && refreshItem.attrs && typeof refreshItem.attrs.onclick === 'function') {
            const originalOnClick = refreshItem.attrs.onclick;
            refreshItem.attrs.onclick = async function (...ocArgs: any[]) {
                try {
                    const maybePromise = originalOnClick.apply(this, ocArgs);
                    await Promise.resolve(maybePromise);
                } finally {
                    if (app.session.user) {
                        BadgeManager.refresh();
                    }
                }
            };
            items.replace('refresh', refreshItem);
        }
    });

    // 当用户执行“全部标记为已读”（保存 markedAllAsReadAt）后，待保存完成再刷新未读角标
    override(User.prototype as any, 'save', function (original: any, attributes: any, options: any) {
        const isMarkAll = attributes && Object.prototype.hasOwnProperty.call(attributes, 'markedAllAsReadAt');
        const result = original(attributes, options);

        if (isMarkAll) {
            Promise.resolve(result).then(() => {
                if (app.session.user) {
                    BadgeManager.refresh();
                }
            });
        }

        return result;
    });
});
